import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { notBlank } from "../../util/ext";
import { equals, filter, lensPath, lensProp, pick, propEq, set } from "ramda";
import TextField from "@material-ui/core/TextField";
import { makeStyles } from "@material-ui/styles";
import {
  validateDetailsField,
  validateDetails,
} from "../organizationValidation";
import Button from "@material-ui/core/Button";
import {
  useBuyingGroups,
  useOrganizationDetailsUpdate,
  useOrganizationProfileNameUpdate,
} from "../organizationServices";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { Skeleton } from "@material-ui/lab";
import { useErrorBox } from "./use-errorBox";

const useStyles = makeStyles(theme => ({
  title: {
    fontSize: "1.8rem",
  },

  multiPicker: {
    "& label.MuiFormLabel-root": {
      backgroundColor: "#fff",
      marginLeft: "-5px",
      padding: "0 6px",
    },
  },
}));

const EditOrganizationDetailsDialog = ({ open, details, onCancel, onSave }) => {
  const classes = useStyles();

  const [fields, setFields] = useState({});
  useEffect(() => {
    if (open && notBlank(details)) {
      setFields(
        pick(
          [
            "organizationId",
            "organizationName",
            "alias",
            "userCreationAllowed",
            "active",
            "priority",
            "buyingGroups",
          ],
          details,
        ),
      );
    } else {
      setFields({});
    }
  }, [open, details]);

  const {
    addValidationError,
    removeValidationError,
    setValidationErrors,
    setGeneralError,
    hasValidationError,
    firstValidationError,
    ErrorBox: DialogErrors,
  } = useErrorBox();

  const [buyingGroups, setBuyingGroups] = useState([]);
  const buyingGroupResponse = useBuyingGroups();
  useEffect(() => {
    if (buyingGroupResponse.isSuccess && notBlank(buyingGroupResponse.data)) {
      setBuyingGroups(filter(propEq("active", true), buyingGroupResponse.data));
    }
  }, [buyingGroupResponse.isSuccess, buyingGroupResponse.data]);

  const handleBlur = (field, value) => {
    const errors = validateDetailsField(field, value);
    if (notBlank(errors)) {
      addValidationError(errors);
    } else {
      removeValidationError(field);
    }
  };

  const handleTextChange = path => event => {
    const value = event.target.value;
    setFields(prev => set(lensPath(path), value, prev));
  };

  const handleCheckboxChange = field => event => {
    const value = event.target.checked;
    setFields(prev => set(lensProp(field), value, prev));
  };

  const handleBuyingGroupsChange = useCallback((_event, orgs) => {
    setFields(prev => set(lensProp("buyingGroups"), orgs, prev));
  }, []);

  const updateDetails = useOrganizationDetailsUpdate();
  const updateProfileName = useOrganizationProfileNameUpdate();
  const handleApply = useCallback(async () => {
    const validationErrors = validateDetails(fields);
    console.log(validationErrors);
    if (notBlank(validationErrors)) {
      setValidationErrors(validationErrors);
    } else {
      try {
        await updateDetails.mutateAsync({
          resourceIds: { id: details.organizationId },
          data: fields,
        });
        await updateProfileName.mutateAsync({
          resourceIds: { id: details.organizationId },
          data: fields.organizationName,
        });
        onSave(fields);
      } catch (err) {
        setGeneralError(err);
      }
    }
  }, [
    fields,
    details.organizationId,
    updateDetails,
    updateProfileName,
    onSave,
    setValidationErrors,
    setGeneralError,
  ]);

  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle id="edit-organization-detail-dialog-title">
        Edit Organization Details
      </DialogTitle>
      <DialogContent>
        <DialogErrors />
        <Grid container spacing={6}>
          <Grid item sm={12}>
            <TextField
              id="organization-name"
              fullWidth
              label="Organization Name"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.organizationName}
              onChange={handleTextChange(["organizationName"])}
              error={hasValidationError("organizationName")}
              helperText={firstValidationError("organizationName")}
              onBlur={() =>
                handleBlur("organizationName", fields.organizationName)
              }
            />
          </Grid>

          <Grid item sm={12}>
            <TextField
              id="organization-alias"
              fullWidth
              label="Alias"
              InputProps={{
                classes: { root: classes.title },
              }}
              value={fields.alias}
              onChange={handleTextChange(["alias"])}
              error={hasValidationError("alias")}
              helperText={firstValidationError("alias")}
              onBlur={() => handleBlur("alias", fields.alias)}
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.active}
                  onChange={handleCheckboxChange("active")}
                  name="organization-active"
                  inputProps={{ "aria-label": "Select" }}
                />
              }
              label="Active"
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.priority}
                  onChange={handleCheckboxChange("priority")}
                  name="organization-priority"
                  inputProps={{ "aria-label": "Select" }}
                />
              }
              label="Priority"
            />
          </Grid>

          <Grid item sm={4}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={fields.userCreationAllowed}
                  onChange={handleCheckboxChange("userCreationAllowed")}
                  name="organization-userCreationAllowed"
                  inputProps={{ "aria-label": "Select" }}
                />
              }
              label="User Creation Allowed"
            />
          </Grid>

          <Grid item xs={12}>
            {notBlank(buyingGroups) ? (
              <Box className={classes.multiPicker}>
                <Autocomplete
                  multiple
                  id="organizations"
                  options={buyingGroups}
                  getOptionLabel={org =>
                    notBlank(org) && `${org.organizationName}`
                  }
                  getOptionSelected={(opt, val) =>
                    notBlank(val) &&
                    equals(opt.organizationId, val.organizationId)
                  }
                  value={fields.buyingGroups}
                  filterSelectedOptions
                  onChange={handleBuyingGroupsChange}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Buying Groups"
                      placeholder="Select..."
                    />
                  )}
                />
              </Box>
            ) : (
              <Skeleton variant="text" />
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditOrganizationDetailsDialog;
